import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Drawer from '@mui/material/Drawer'
import { makeStyles } from '@mui/styles'
import ThemeInterface from '@/theme-interface'
import { closeAvailableOcrModal, closeCustomerSupplierModal, closePaymentDrawer, DrawerTab, setAnalysisActive, setContactFormEnabled, updateContact, fetchDocumentDetails, closeDocumentDrawer } from '@/redux/ducks/document/drawer'
import lang from '@/lang'
import clsx from 'clsx'
import ReceiptApplyDataModal from '@/modules/receipt/apply-data-modal/receipt-apply-data-modal'
import SupplierCustomerAddressFormModal from '@/modules/base/components/generics/forms/address/address-form-modal'
import checkAccessAndRights from '@/modules/_helpers/checkAccessAndRights'
import { mimeTypes, USER_RIGHTS, SUBSCRIPTION_MODULES, experimentalFeatures } from '@/modules/base/util/constants'
import { user } from 'ampersand-app' // FLAG:useDocuAi — read the per-tenant experimental flag
import Loader from '@/modules/base/components/loader/loader'
import DocumentInfoGeneral from '@/modules/document/components/file-info/tabs/document-info-general'
import IncomingInvoiceDetails from '@/modules/document/components/file-info/tabs/incoming-invoice-details'
import DocumentDrawerHeader from '@/modules/document/components/document-drawer/document-drawer-header'
import DocumentDrawerTabs from '@/modules/document/components/document-drawer/document-drawer-tabs'
import DocumentDrawerFooter from '@/modules/document/components/document-drawer/document-drawer-footer'
// FLAG:docuAiEnhancements — collapsible receipt help chat (renders null flag-OFF).
import RagChatSection from '@/modules/document/components/document-drawer/rag-chat-section'
import DocumentActionsButton from '@/modules/document/components/file-info/actions/document-actions-button'
import DocumentCarouselWrapper from '@/modules/document/components/document-drawer/document-carousel-wrapper'
import QRPaymentDrawer from '@/modules/document/components/edit/qr-payment-drawer'
import { saveDocument } from '@/redux/ducks/document/documentDrawerHelpers'
import { createSupplierCustomer } from '@/modules/document/_helpers/createSupplierCustomer'
import { resolveAnalysedPreserveTags } from '@/modules/document/_helpers/resolveAnalysedPreserveTags' // FLAG:useDocuAi
import useWebSocketEvent, { EventTypes } from '@/modules/webSocket'
import { CssHelper } from '@/modules/base/view/mixin/helpers_mixin'
import { useAppDispatch, useAppSelector } from '@/redux/store'
import checkOcrValues from '@/modules/document/_helpers/checkOcrValues'
import { toast } from '@/modules/base/components/toast/toastConfig'
import { DocumentAnalysisService } from '@/modules/document/services/documentAnalysis'
import DocumentDrawerRetryingBanner from '@/modules/document/components/document-drawer/document-drawer-retrying-banner'
import { isReviewableInvoiceArt } from '@/modules/document/utils/document-invoice-kind' // FLAG:useDocuAi
import { isMediaMimetype } from '@/modules/document/utils/media-preview' // FLAG:useDocuAi
import { initialPreviewGate, nextPreviewGate, PreviewGateMemory } from '@/modules/document/utils/document-preview-gate' // FLAG:useDocuAi
import { ackUiEvent } from '@/modules/document/utils/ui-event-ack'
import { useUpdateSearchParams } from '@/modules/_helpers/updateSearchParams'

const {
  MIMETYPE_JPG,
  MIMETYPE_JPEG,
  MIMETYPE_PNG,
  MIMETYPE_BMP,
  MIMETYPE_PDF,
  MIMETYPE_TEXT_XML,
  MIMETYPE_DOC,
  MIMETYPE_DOCX,
  MIMETYPE_XLS,
  MIMETYPE_XLSX,
  MIMETYPE_PPT,
  MIMETYPE_PPTX,
} = mimeTypes

export const previewMimeTypes = [
  MIMETYPE_JPG,
  MIMETYPE_JPEG,
  MIMETYPE_PNG,
  MIMETYPE_BMP,
  MIMETYPE_PDF,
  MIMETYPE_TEXT_XML,
  MIMETYPE_DOC,
  MIMETYPE_DOCX,
  MIMETYPE_XLS,
  MIMETYPE_XLSX,
  MIMETYPE_PPT,
  MIMETYPE_PPTX,
]

// FLAG:useDocuAi — user-resizable split between the document preview and the review
// panel. The panel opens at the default width every time (never persisted) and can be
// dragged wider or narrower; the width stays an explicit number so long content still
// can't grow it.
// The default is driven by the widest row the panel has to show — a line item:
// 72 (VAT) + 78 (VAT amount) + 88 (price) + 32 (delete) are fixed, and the category
// select needs ~205 for its placeholder + dropdown arrow PLUS 30 for the `hint` icon
// (select-input.css `.toolTipLogo`, rendered when the category is locked) — that icon is
// a flex sibling INSIDE the category field, not an overlay, so it eats real width. 660
// (the form-column maxWidth the other two hosts use: embedded-invoice-review.tsx
// `formColumn`, receipt-edit.tsx `form`) leaves the row 14px short and clips the icon in
// half; 720 clears it with room for longer category names.
const DEFAULT_DETAILS_WIDTH = 720
const MIN_DETAILS_WIDTH = 510
const MAX_DETAILS_WIDTH = 1100

// Same clamp for the drag and for the width the panel opens at, so a narrow window never
// gets a default panel that is wider than what the user could drag it to. Browser-only —
// reads globalThis.innerWidth, so call it from effects/handlers, never during render.
const clampDetailsWidth = (width: number) => Math.min(
  Math.min(MAX_DETAILS_WIDTH, Math.round(globalThis.innerWidth * 0.8)),
  Math.max(MIN_DETAILS_WIDTH, width),
)

interface StyleProps {
  hasNoPreview?: boolean
  detailsWidth?: number
  resizableDetails?: boolean
}

const useStyles = makeStyles((theme: ThemeInterface) => ({
  drawer: {
    display: 'flex',
    flexDirection: 'row',
    // width: 'auto',
    flexShrink: 1,
    '&.hasNoPreview': {
      width: '510px',
      minWidth: '510px',
      maxWidth: '510px',
      overflow: 'visible',
      transition: 'none',
    },
  },
  content: {
    display: 'flex',
    height: '100vh',
    width: ({ hasNoPreview }: StyleProps) => hasNoPreview ? '510px' : '100vw',
    transition: 'width 0.4s ease-out',
    minWidth: '510px',
    overflow: 'hidden', // Prevent app-level overflow
    position: 'relative',
  },
  preview: {
    flexGrow: 1,
    display: 'flex',
    overflow: 'hidden', // Prevent overflow from preview area
    position: 'relative',
  },
  // FLAG:useDocuAi — transient-retry banner (plan docuai-analysis-retrying). Rendered ONLY
  // while metaData.analysisRetrying exists, which flag-OFF tenants can never carry (the
  // worker short-circuits before writing it) — flag-OFF DOM stays byte-identical.
  retryingBanner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    margin: '8px 16px 0',
    padding: '8px 12px',
    borderRadius: 4,
    border: `1px solid ${theme.primary}`,
    fontSize: 13,
    lineHeight: 1.35,
  },
  retryingBannerButton: {
    border: 'none',
    background: 'transparent',
    color: theme.primary,
    cursor: 'pointer',
    fontWeight: 600,
    whiteSpace: 'nowrap',
    padding: 0,
  },
  details: ({ detailsWidth = DEFAULT_DETAILS_WIDTH, resizableDetails }: StyleProps) => ({
    display: 'flex',
    flexDirection: 'column',
    borderLeft: theme.border,
    borderTop: theme.border,
    position: 'relative',
    zIndex: 1100,
    backgroundColor: theme.colors.white, // Solid background to block carousel content
    // FLAG:useDocuAi — only the useDocuAi review gives the panel an explicit, resizable
    // width (default DEFAULT_DETAILS_WIDTH): it stops long position text from growing the
    // drawer and backs the divider handle. Flag-OFF omits these entirely → the baseline
    // content-sized panel, byte-identical to before the feature.
    ...(resizableDetails
      ? {
        flex: `0 0 ${detailsWidth}px`,
        width: detailsWidth,
        minWidth: detailsWidth,
        maxWidth: detailsWidth,
        // FLAG:useDocuAi — make the "OCR-Analyse Ergebnis" header line up pixel-exact with the
        // document title bar on the left (both theme.backgroundColor grey). Baseline: the panel's
        // 1px borderTop pushes the header down 1px and its height (86) is 1px under the title (87),
        // so the right grey read ~1px shorter/lower. Flag-ON only: drop that top border and bump
        // the header to 87 (targeting the <header> tag PanelHeader renders — no JSS-class reliance,
        // so it holds in production). Flag-OFF keeps borderTop + the 86px header, byte-identical.
        borderTop: 'none',
        '& > header': {
          height: 87,
        },
      }
      : {}),
  }),
  // Draggable divider between preview and details. The col-resize cursor is the "two
  // arrows" grip; a thin visible bar makes it discoverable.
  // FLAG:useDocuAi — the handle grey matches the two flanking headers (preview title bar +
  // "OCR-Analyse Ergebnis" details header, both theme.backgroundColor) so the seam reads as one
  // continuous grey band instead of a white gap. Only ever rendered flag-ON, so baseline is untouched.
  resizeHandle: {
    flex: '0 0 7px',
    cursor: 'col-resize',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: theme.backgroundColor,
    position: 'relative',
    zIndex: theme.zIndex.drawer,
    '&:hover > div, &:active > div': {
      background: theme.primary,
      width: 4,
    },
  },
  resizeGrip: {
    width: 2,
    height: 44,
    borderRadius: 2,
    background: theme.borderColor,
    transition: 'background 0.15s, width 0.15s',
  },
  // While dragging, a full-window overlay keeps mouse events from being swallowed by the
  // preview iframe/canvas and keeps the resize cursor everywhere.
  resizeOverlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 2000,
    cursor: 'col-resize',
  },
  detailsContent: {
    flex: '1 1 0',
    minWidth: 510,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.colors.white, // Solid background to block carousel content
  },
  tabsWrapper: {
    position: 'relative',
    overflow: 'hidden',
    flex: '1 0 0',
  },
  tabContent: {
    position: 'absolute',
    transition: 'all 0.2s ease-out',
    width: '100%',
    height: '100%',
    left: 0,
    '&.left': {
      transform: 'translateX(-100%)',
    },
    '&.right': {
      transform: 'translateX(100%)',
    },
  },
  loader: {
    background: CssHelper.alpha(theme.backgroundColor, 0.6),
    width: '100%',
  },
}))

const DocumentDrawer = (props) => {
  const dispatch = useAppDispatch()
  const { refreshList } = props
  const {
    open,
    document,
    analysisActive,
    activeTab,
    availableOcrModalOpen,
    contactModalOpen,
    contactFormData,
    isSaving,
    contactFormEnabled,
    analyze,
    isLoading,
    page,
    // FLAG:useDocuAi — matched-child preselect for the preview switcher (search rollup).
    preselectChildVersionId,
  } = useAppSelector((state) => state.document.drawer)

  const [ocrProcessingState, setOcrProcessingState] = useState({
    initialOcrApplied: false,
    shouldApplyOcr: true,
    processingComplete: false,
  })

  // FLAG:useDocuAi — resizable preview/review split. Opens at the default width every time
  // (reset below on open / document change), draggable via the divider handle.
  const [detailsWidth, setDetailsWidth] = useState(DEFAULT_DETAILS_WIDTH)
  const [resizing, setResizing] = useState(false)

  const startResize = useCallback((event: React.MouseEvent) => {
    event.preventDefault()
    setResizing(true)
    const onMove = (moveEvent: MouseEvent) => {
      setDetailsWidth(clampDetailsWidth(globalThis.innerWidth - moveEvent.clientX))
    }
    const onUp = () => {
      setResizing(false)
      globalThis.removeEventListener('mousemove', onMove)
      globalThis.removeEventListener('mouseup', onUp)
    }
    globalThis.addEventListener('mousemove', onMove)
    globalThis.addEventListener('mouseup', onUp)
  }, [])

  // FLAG:useDocuAi — imperative handle of the drawer-embedded central review (Details tab of a
  // pending-review invoice). The footer's Save delegates to it (see document-drawer-footer.tsx).
  const reviewSaveRef: React.MutableRefObject<{ onSave: () => void } | null> = useRef(null)

  const [, updateSearchParams] = useUpdateSearchParams()
  // FLAG:useDocuAi — set ⇒ this tenant's files are served from the DMS and therefore have DMS
  // thumbnails; that is what makes an archive previewable at all (see previewableViaDocuAi).
  const dmsThumbnailUrl = useAppSelector((state) => state.tenant.settings.dmsThumbnailUrl)

  const ocrState = useMemo(() => {
    if (!document) return null
    return checkOcrValues(document?.expense, document?.revenue, document)
  }, [
    document?.expense,
    document?.revenue,
    document?.documentInvoiceDetails,
    document?.metaData?.ocr?.isRunning,
    document?.metaData?.ocr?.priceTotal,
    document?.metaData?.ocr?.receiptNumber,
    document?.metaData?.ocr?.date,
    document?.metaData?.ocr?.paymentDueDate,
    document?.metaData?.ocr?.payedAt,
    document?.metaData?.ocr?.payee,
    document?.metaData?.ocr?.payeeIban,
    document?.metaData?.ocr?.payeeBic,
  ])

  useEffect(() => {
    return () => {
      dispatch(closeDocumentDrawer())
      dispatch(closeAvailableOcrModal())
      dispatch(setAnalysisActive(false))
    }
  }, [])

  useEffect(() => {
    if (analysisActive) {
      setOcrProcessingState({
        initialOcrApplied: false,
        shouldApplyOcr: true,
        processingComplete: false,
      })
    }
  }, [analysisActive])

  const handleAnalyzedEvent = useCallback(({ data }) => {
    const { id, autoClassifiedAs } = data
    if (document && document.id === id) {
      const currentKind = document?.tags?.find(({ isSystem }) => !!isSystem)

      // FLAG:useDocuAi — when the worker auto-set the Art (autoClassifiedAs), drop
      // the stale system tag so the refetch adopts the freshly classified one.
      // flag-OFF (or no autoClassifiedAs) keeps the baseline preserve-tag behaviour.
      const useDocuAiEnabled = user?.experimentalFeatures?.includes(experimentalFeatures.useDocuAi)
      const preserveTags = resolveAnalysedPreserveTags({ useDocuAiEnabled, autoClassifiedAs, currentKind })

      dispatch(fetchDocumentDetails({
        documentId: id,
        analyze: false,
        rename: false,
        analysisDone: true,
        analysisActive: false,
        preserveTags,
      }))

      dispatch(setAnalysisActive(false))

      setOcrProcessingState({
        initialOcrApplied: false,
        shouldApplyOcr: true,
        processingComplete: false,
      })

      // FLAG:useDocuAi — the open drawer is one of the FILE:ANALYSED consumers, so it acks the
      // terminal it just applied (see document/utils/ui-event-ack: every consumer must ack, or the
      // delivery row stays outstanding and is re-broadcast until the attempt cap). Inside the
      // document.id === id branch, so we only ever ack the event for the document we handled.
      // No-op for the start broadcast, which carries no eventId.
      ackUiEvent(data?.eventId)
    }
  }, [document])

  // FLAG:useDocuAi — refresh the open document when DocuAI image stats arrive.
  // Inert on baseline: the DMS→omxweb worker only emits FILE:IMAGE_STATS:DMS for
  // useDocuAi tenants, so this callback never fires with the flag off.
  const handleImageStatsEvent = useCallback(({ data }) => {
    if (document && document.id === data?.fileId) {
      const currentKind = document?.tags?.find(({ isSystem }) => !!isSystem)
      dispatch(fetchDocumentDetails({
        documentId: document.id,
        analyze: false,
        rename: false,
        preserveTags: currentKind ? [currentKind] : [],
      }))
    }
  }, [document])

  const handleAccessUpdated = useCallback(() => {
    if (!open || !document?.id) {
      return
    }

    dispatch(fetchDocumentDetails({
      documentId: document.id,
      analyze: false,
      rename: false,
      activeTab,
      rowIndex: typeof page?.offset === 'number' ? page.offset - 1 : undefined,
      permissionRefresh: true,
    }))
  }, [activeTab, dispatch, document?.id, open, page?.offset])

  useEffect(() => {
    if (
      ocrProcessingState.shouldApplyOcr
      && document?.metaData?.ocr
      && !document?.metaData?.ocr?.isRunning
      && !ocrProcessingState.initialOcrApplied
      && !analysisActive
    ) {
      setOcrProcessingState(prev => ({
        ...prev,
        shouldApplyOcr: false,
        initialOcrApplied: true,
      }))
    }
  }, [
    document?.metaData?.ocr,
    ocrState?.hasUncheckedOcrValues,
    ocrProcessingState.shouldApplyOcr,
    ocrProcessingState.initialOcrApplied,
    analysisActive,
  ])

  useEffect(() => {
    if (document?.id) {
      setOcrProcessingState({
        initialOcrApplied: false,
        shouldApplyOcr: true,
        processingComplete: false,
      })
    }
  }, [document?.id])

  const hasSupplierRights = checkAccessAndRights(SUBSCRIPTION_MODULES.SUPPLIERS, USER_RIGHTS.SUPPLIERS.WRITE)
  const hasCustomerRights = checkAccessAndRights(SUBSCRIPTION_MODULES.CUSTOMERS, USER_RIGHTS.CUSTOMERS.WRITE)
  // FLAG:useDocuAi — the resizable preview/review split (and its fixed-width panel) is a
  // useDocuAi-review-only enhancement. Flag-OFF keeps the baseline drawer exactly: content-
  // sized details panel, no divider handle.
  const docuAiReviewEnabled = !!user?.experimentalFeatures?.includes(experimentalFeatures.useDocuAi)

  // FLAG:useDocuAi — widen what counts as previewable.
  //
  // `previewMimeTypes` is a SECOND, coarser allowlist sitting above document-drawer-preview's
  // own routing, and it decides whether the preview pane is MOUNTED AT ALL: when it says no,
  // the drawer collapses to the 510px details-only panel. Video and archives are not in it, so
  // the media player and the archive thumbnail could never appear no matter what the preview
  // component decided — the component was never rendered. Two additions, both flag-gated so
  // flag-OFF keeps the exact baseline drawer:
  //   · video/audio  -> the ported media player (document-drawer-preview-media.tsx),
  //   · any DMS-backed file -> its DMS thumbnail (archives get an "Archiv-Vorschau"), falling
  //     back to the mime icon when the DMS has no thumbnail for it.
  // Shared with document-drawer-preview's `isPlayableMedia` (media-preview.ts). This gate and
  // that one MUST agree on what counts as media: this one only mounts the pane, that one picks
  // the viewer inside it, and when they disagreed the preview flickered — see media-preview.ts.
  const previewableViaDocuAi = docuAiReviewEnabled
    && (isMediaMimetype(document?.mimetype) || (!!dmsThumbnailUrl && !!document?.minioId))
  const hasNoPreviewNow = !document || !!document.uploadToken
    || (!previewMimeTypes.includes(document.mimetype) && !previewableViaDocuAi)

  // FLAG:useDocuAi — hold the verdict steady while the SAME document is being refreshed. Flipping
  // it slides the whole drawer (510px ↔ 100vw over 0.4s), and the refetch storm after an upload
  // flips it repeatedly — worst for video, which has no `previewMimeTypes` entry to fall back on.
  // See document-preview-gate.ts. Flag-OFF this is skipped entirely and `hasNoPreview` is the
  // unchanged expression above.
  const previewGateRef = useRef<PreviewGateMemory>(initialPreviewGate)
  let hasNoPreview = hasNoPreviewNow
  if (docuAiReviewEnabled) {
    const gate = nextPreviewGate(previewGateRef.current, document?.id, hasNoPreviewNow)
    previewGateRef.current = gate.memory
    hasNoPreview = gate.hasNoPreview
  }

  useWebSocketEvent(EventTypes.FILE_ANALYZED, handleAnalyzedEvent)
  useWebSocketEvent(EventTypes.FILE_IMAGE_STATS_DMS, handleImageStatsEvent) // FLAG:useDocuAi
  useWebSocketEvent(EventTypes.ACCESS_UPDATED, handleAccessUpdated)
  useWebSocketEvent(EventTypes.NO_ANALYSE_RESULT, () => toast.showToast({
    message: lang.documents.noAnalyseResult,
    type: 'info',
  }))

  useEffect(() => {
    if (
      document?.metaData?.ocr
      && !document?.metaData?.ocr?.isRunning
      && !document?.metaData?.ocr?.error
      && !ocrState?.hasValidValues
      && !ocrState?.hasUncheckedOcrValues
      && document?.metaData?.ocr?.autoscan === 'used'
    ) {
      toast.showToast({
        message: lang.documents.noOcrResultsFound,
        type: 'info',
      })
    }
  }, [
    document?.metaData?.ocr,
    document?.metaData?.ocr?.isRunning,
    document?.metaData?.ocr?.error,
    document?.metaData?.ocr?.autoscan,
    ocrState?.hasValidValues,
    ocrState?.hasUncheckedOcrValues,
  ])

  useEffect(() => {
    // Force a re-render to apply the new width when hasNoPreview changes
    const drawerPaper = globalThis.document.querySelector('.MuiDrawer-paper')
    if (drawerPaper) {
      drawerPaper.classList.toggle('hasNoPreview', hasNoPreview)
    }
  }, [hasNoPreview])

  // FLAG:useDocuAi — always open at the default split width (never persist a drag).
  useEffect(() => {
    setDetailsWidth(clampDetailsWidth(DEFAULT_DETAILS_WIDTH))
  }, [open, document?.id])

  const classes = useStyles({ ...props, hasNoPreview, detailsWidth, resizableDetails: docuAiReviewEnabled })

  useEffect(() => {
    if (!open) return
    dispatch(setContactFormEnabled(hasSupplierRights || hasCustomerRights))
  }, [open, hasSupplierRights, hasCustomerRights])

  useEffect(() => {
    if (analyze) {
      DocumentAnalysisService.analyze({ skipModal: true, isReanalyze: !!document?.metaData?.ocr })
    }
  }, [analyze])

  // Backdrop click / ESC. Must do exactly what the header's X does (document-drawer-header.tsx):
  // clearing the URL params ALONE left `document.drawer.open` true, so the drawer stayed on
  // screen — the list only syncs redux → URL (`if (!drawerOpen) updateSearchParams(...)`),
  // nothing watches the URL to close the drawer. `saveDocument` short-circuits to the callback
  // when nothing changed, so a clean close still closes immediately without a prompt.
  const onClose = () => {
    saveDocument(() => {
      updateSearchParams([{ key: 'documentId' }, { key: 'rowIndex' }])
      dispatch(closeDocumentDrawer())
    }, true)
  }

  let drawerContent = null
  let additionalContent = null
  if (document) {
    const tabs: DrawerTab[] = [
      {
        label: lang.generics.general,
        ocrContent: false,
        content: <DocumentInfoGeneral />,
      },
    ]

    const kind = document.tags.find(({ isSystem }) => !!isSystem)
    // FLAG:useDocuAi — the Details tab IS the host of the review component, and a cancellation invoice
    // is reviewable like any other incoming invoice (isReviewableInvoiceArt explains this and excludes
    // the tenant's own outgoing cancellation invoice). The first term is the unchanged baseline:
    // with the flag OFF only the „Rechnungseingang" label still decides, while the second term is
    // false there by construction.
    const hostsInvoiceDetails = kind?.label === lang.tags.INCOMING_INVOICE
      || (docuAiReviewEnabled && isReviewableInvoiceArt(document))
    if (hostsInvoiceDetails) {
      tabs.push({
        label: lang.generics.details,
        ocrContent: true,
        content: <IncomingInvoiceDetails reviewSaveRef={reviewSaveRef} refreshList={refreshList} />,
      })
    }

    const details = (<>
      {analysisActive && <Loader className={classes.loader} text={lang.documents.scanning} onCancel={() => dispatch(setAnalysisActive(false))} />}
      <DocumentDrawerHeader />
      <DocumentDrawerRetryingBanner
        enabled={docuAiReviewEnabled}
        document={document}
        className={classes.retryingBanner}
        buttonClassName={classes.retryingBannerButton}
      />
      <div className={classes.detailsContent}>
        {<DocumentDrawerTabs ocrState={ocrState} tabs={tabs} document={document} />}
        <div className={classes.tabsWrapper}>
          {tabs.map(({ content }, i) => <div className={clsx(classes.tabContent, { active: activeTab === i, left: activeTab > i, right: activeTab < i })} key={`tabContent ${i}`}>{content}</div>)}
        </div>
      </div>
      <RagChatSection />
      <DocumentDrawerFooter refreshList={refreshList} reviewSaveRef={reviewSaveRef}>
        {tabs?.[activeTab]?.actions ?? <DocumentActionsButton refreshList={props.refreshList} />}
      </DocumentDrawerFooter>
    </>)

    drawerContent = (<>
      <div className={classes.content}>
        {!hasNoPreview && (
          <div className={classes.preview}>
            <DocumentCarouselWrapper document={document} fitToPane={docuAiReviewEnabled} preselectChildVersionId={preselectChildVersionId} />
          </div>
        )}
        {!hasNoPreview && docuAiReviewEnabled && (
          <div
            className={classes.resizeHandle}
            onMouseDown={startResize}
            role='separator'
            aria-orientation='vertical'
            aria-label='Breite ziehen'
            title='Breite ziehen'
          >
            <div className={classes.resizeGrip} />
          </div>
        )}
        {resizing && docuAiReviewEnabled && <div className={classes.resizeOverlay} />}
        <div className={classes.details}>
          {details}
        </div>
        {/* FLAG:useDocuAi — analyze() is the legacy door: with the flag ON the same call would
            start a real DocuAI run with LLM costs (F12.1). Therefore scan does nothing with the flag
            ON (the drawer button is disabled then, and automatic analysis fills the fields); with
            the flag OFF it remains the baseline trigger — exactly ONCE, as the drawer now invokes
            only this onScan instead of additionally analyzing itself. */}
        <QRPaymentDrawer
          onClose={() => dispatch(closePaymentDrawer())}
          file={document}
          onScan={() => { if (!docuAiReviewEnabled) DocumentAnalysisService.analyze({ skipModal: true }) }}
          isScanning={analysisActive} />
      </div>
    </>)

    additionalContent = (<>
      {isSaving && <Loader className={classes.loader} text={lang.generics.saving} />}
      {contactModalOpen && <SupplierCustomerAddressFormModal onSubmit={([data]) => createSupplierCustomer(data, (contact) => dispatch(updateContact(contact)))}
        onCancel={() => dispatch(closeCustomerSupplierModal())} data={contactFormData} onClose={() => dispatch(closeCustomerSupplierModal())} />}
      {availableOcrModalOpen
        ? <ReceiptApplyDataModal
          contactFormEnabled={contactFormEnabled}
          onApplyData={() => dispatch(closeAvailableOcrModal())}
          fileToCheck={document}
          preventReset
          onCancel={() => {
            dispatch(closeAvailableOcrModal())
            DocumentAnalysisService.analyze({
              skipModal: true,
              retryFailed: document.metaData?.analysisFailed === true,
            })
          }}
          onPanic={() => dispatch(closeAvailableOcrModal())}
          titleLabel={lang.documents.applyDataModal.title}
          descriptionLabel={lang.documents.applyDataModal.description}
          cancelLabel={lang.documents.applyDataModal.confirmNo}
          confirmLabel={lang.documents.applyDataModal.cancelAndScanAgain}
          swapButtons={true}
        />
        : null}
    </>)
  }

  return (
    <>
      <Drawer
        classes={{ paper: classes.drawer }}
        anchor="right"
        transitionDuration={400}
        open={open}
        onClose={onClose}
      >
        {isLoading && !analysisActive && <Loader className={classes.loader} text={lang.generics.loading} />}
        {drawerContent}
      </Drawer>
      {additionalContent}
    </>
  )
}

export default React.memo(DocumentDrawer)
